/**
 * React hook for checking PostHog feature flag variants
 */

import { useState, useEffect, useMemo } from 'react';
import { usePosthog } from './usePosthog';

/**
 * Hook to check if a feature flag is set to a specific variant
 * Returns true if the flag is set to the expected variant, false otherwise
 */
export const useFeatureFlagVariant = (
  flagKey: string,
  expectedVariant: string,
): boolean => {
  const posthogService = usePosthog();

  // Get initial variant value immediately to avoid flash of false
  const initialValue = useMemo(
    () => posthogService?.isFeatureFlagVariant(flagKey, expectedVariant) ?? false,
    [posthogService, flagKey, expectedVariant],
  );

  const [isVariant, setIsVariant] = useState<boolean>(initialValue);

  useEffect(() => {
    if (!posthogService) return;

    // Sync current value in case it changed since initial render
    const currentValue = posthogService.isFeatureFlagVariant(flagKey, expectedVariant);
    setIsVariant(currentValue);

    // Subscribe to flag changes
    posthogService.onFeatureFlags(() => {
      setIsVariant(posthogService.isFeatureFlagVariant(flagKey, expectedVariant));
    });
  }, [flagKey, expectedVariant, posthogService]);

  return isVariant;
};